import React, { useEffect, useState } from 'react';
import { FormControlLabel, Radio, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import { getUserNotifications } from '@/api/notifications.api';
import { loginMember, loginToken, resendLoginCode, switchLang } from '@/api/auth.api';
import { getExternalProviders, startExternalAuth } from '@/api/externalAuth.api';
import type { ExternalProviderDescriptor } from '@/types/externalAuth';
import { issuePblCitizenSmsLoginOtp, verifyPblCitizenSmsLoginOtp } from '@/api/pbl.api';
import TurnstileWidget from '@/components/TurnstileWidget';
import { safeInternalReturnPath } from '@/utils/safeReturnPath';
import { getTurnstileSiteKey } from '@/utils/turnstile';
import { useDispatch } from 'react-redux';
import { getAuthSelector, signIn } from '@/redux/auth/auth.slice';
import { getNotification } from '@/redux/notification/notification.slice';
import { useAppSelector } from '@/redux/store.hook';

import { InputWrapper, MultiInputWrapper } from '@/components/Container/AuthContainer/styles';
import { Button } from '@/components/UI/Button';
import { Text } from '@/components/UI/Typography/Text';
import StyledLink from '@/components/UI/Link';
import { Box } from '@/layout/Box';
import { Flex } from '@/layout/Flex';

import PATH from '@/router/path';
import useAuthValidation from '@/hooks/requireSchema/useAuthValidation';
import { errorHandler } from '@/utils/toastHandler';
import { LoadingPage } from '@/layout';
import useResize from '@/hooks/useResize';
import { useGetPolicyTerms } from '@/hooks/useGetPolicyTerms';
import { FormSelect } from '@/components/UI/Select';
import { isPblUser } from '@/utils/pblRole';
import { getAuthRefreshDeadline } from '@/utils/authToken';
import type { PblAuthTokenResponse } from '@/types/pbl';
import { resolvePblLoginRedirect } from '../pblLoginRedirect';
import { resolveDmpPurchaseReturnPath, withDmpPurchaseReturnPath } from '@/components/DmpV2/purchaseReturnPath';
import { v2UiEnabled } from '@/components/DmpV2/features';
import ExternalProviderOptions from '../../ExternalProviderOptions';
import { StatusMessage } from '../../externalStyles';

interface LoginDataProps {
  email: string;
  password: string;
  token: string;
  resendAvailableAt?: string;
}

interface Step1Props {
  setSteps: React.Dispatch<React.SetStateAction<number>>;
  setLoginData: React.Dispatch<React.SetStateAction<LoginDataProps>>;
}

type PblCitizenPasswordlessStatus = {
  tone: 'success' | 'error';
  message: string;
};

type Step1LoginMode = 'emailOtp' | 'smsOtp' | 'pblCitizenSmsOtp';

const HONG_KONG_COUNTRY_CODE_OPTIONS = [{ value: '+852', text: '+852' }];

const normalizeHongKongMobile = (value: string) => {
  const compactValue = value.replace(/[\s-]/g, '').trim();
  let phone = compactValue;

  if (phone.startsWith('+852')) {
    phone = phone.slice(4);
  } else if (phone.startsWith('852') && phone.length === 11) {
    phone = phone.slice(3);
  }

  if (!/^[4-9]\d{7}$/.test(phone)) {
    return null;
  }

  return {
    phone_country_code: '+852',
    phone,
  };
};

const getApiErrorMessage = (error: any, fallback: string) => (error.response ? fallback : error.message || fallback);

const shouldShowPblCitizenPasswordlessLogin = () => {
  const override = process.env.REACT_APP_SHOW_PBL_CITIZEN_SMS_LOGIN;

  if (override === 'true') {
    return true;
  }

  if (override === 'false') {
    return false;
  }

  return true;
};

const shouldShowMemberSmsOtpLogin = () => {
  return true;
  const override = process.env.REACT_APP_SHOW_MEMBER_SMS_OTP_LOGIN;

  if (override === 'false') {
    return false;
  }

  return true;
};

const Step1 = ({ setSteps, setLoginData }: Step1Props) => {
  const navigate = useNavigate();
  const { isMobile } = useResize();
  const auth = useAppSelector(getAuthSelector);
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [announce, setAnnounce] = useState(true);
  const [failedCounter, setFailedCounter] = useState(0);
  const [loginMode, setLoginMode] = useState<Step1LoginMode>('emailOtp');
  const [citizenPhone, setCitizenPhone] = useState('');
  const [citizenIdentityPartial, setCitizenIdentityPartial] = useState('');
  const [citizenPasswordlessStatus, setCitizenPasswordlessStatus] = useState<PblCitizenPasswordlessStatus | null>(null);
  const [citizenSmsOtp, setCitizenSmsOtp] = useState('');
  const [isRequestingCitizenOtp, setIsRequestingCitizenOtp] = useState(false);
  const [isVerifyingCitizenOtp, setIsVerifyingCitizenOtp] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [externalProviders, setExternalProviders] = useState<ExternalProviderDescriptor[]>([]);

  // @ts-ignore
  const { t, i18n } = useTranslation();
  const showPblCitizenPasswordlessLogin = shouldShowPblCitizenPasswordlessLogin();
  const showMemberSmsOtpLogin = shouldShowMemberSmsOtpLogin();
  const showCitizenSmsLoginForm = showPblCitizenPasswordlessLogin && loginMode === 'pblCitizenSmsOtp';
  const turnstileSiteKey = getTurnstileSiteKey();
  const useOtpV2 = process.env.REACT_APP_AUTH_OTP_V2_ENABLED === 'true';

  const externalAuthEnabled = process.env.REACT_APP_EXTERNAL_AUTH_ENABLED === 'true';

  useEffect(() => {
    if (externalAuthEnabled) {
      void getExternalProviders()
        .then((response) => setExternalProviders(response.data.data.providers.filter((item) => item.enabled_for_login)))
        .catch(() => setExternalProviders([]));
    } else if (process.env.REACT_APP_WECHAT_OAUTH_ENABLED === 'true') {
      setExternalProviders([
        {
          key: 'wechat',
          display_name: { en: 'WeChat', tc: '微信', sc: '微信' },
          enabled_for_login: true,
          enabled_for_registration: false,
          enabled_for_linking: true,
          enabled_for_reauthentication: true,
          supported_capabilities: ['login'],
        },
      ]);
    }
  }, [externalAuthEnabled]);

  const beginExternalLogin = async (provider: string) => {
    setIsLoading(true);
    try {
      const response = await startExternalAuth(
        provider,
        'login',
        safeInternalReturnPath(searchParams.get('path')),
        turnstileToken
      );
      window.location.assign(response.data.data.authorization_url);
    } catch (error: any) {
      errorHandler(error.response?.data?.message || t('pages.auth.external.failed'));
      setIsLoading(false);
      if (turnstileSiteKey) resetTurnstile();
    }
  };

  useEffect(() => {
    const path = searchParams.get('path');

    if (path && announce) {
      setAnnounce(false);
      errorHandler(t('error.login'));
      return;
    }

    if (!path && auth.userData.name && auth.isLogin) {
      navigate('/home', { replace: true });
    }
  }, []);

  const {
    handleSubmit,
    control,
    setError,
    formState: { isDirty },
  } = useForm<MemberSignInFormFields>({ shouldUnregister: true });

  const { emailRegister, passwordRegister } = useAuthValidation();

  const handleLoginModeChange = (nextMode: Step1LoginMode) => {
    setLoginMode(nextMode);
    setCitizenPasswordlessStatus(null);
    setCitizenSmsOtp('');
    setTurnstileToken(null);
    setTurnstileResetKey((current) => current + 1);
  };

  const handleTurnstileTokenChange = React.useCallback((token: string | null) => setTurnstileToken(token), []);

  const resetTurnstile = () => {
    setTurnstileToken(null);
    setTurnstileResetKey((current) => current + 1);
  };

  const onSubmit = handleSubmit(async (data) => {
    setIsLoading(true);

    try {
      const otpMethod = loginMode === 'smsOtp' ? 'sms' : 'email';
      const payload: MemberSignInFormRequest = {
        email: data.email,
        password: CryptoJS.MD5(data.password).toString(),
        otp_method: otpMethod,
        ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
      };

      if (otpMethod === 'sms' && !useOtpV2) {
        payload.phone_country_code = data.phone_country_code?.split(' ')[0].replace('+', '');
        payload.phone_number = data.phone_number?.trim();
      }

      const res = (await loginMember(payload)).data;

      if (res.success) {
        setLoginData({
          token: res.data.one_time_token,
          email: data.email,
          password: CryptoJS.MD5(data.password).toString(),
          resendAvailableAt: res.data.resend_available_at,
        });
        setSteps(2);
      }
    } catch (error: any) {
      const errorData = error.response?.data?.data;
      const responseMessage =
        error.response?.data?.errors?.turnstile_token?.[0] || error.response?.data?.message || t('error.email');

      if (errorData && typeof errorData === 'object' && errorData[0] !== 'Unauthorized') {
        setFailedCounter(6);
        setError('email', {
          type: 'server',
          message: responseMessage,
        });
        return;
      }

      if (failedCounter + 1 >= 6) {
        setError('email', {
          type: 'server',
          message: responseMessage,
        });
        return;
      }

      setFailedCounter(failedCounter + 1);
      setError('email', {
        type: 'server',
        message: t('error.email'),
      });
      setError('password', {
        type: 'server',
        message: t('error.password'),
      });
    } finally {
      setIsLoading(false);
      if (turnstileSiteKey) resetTurnstile();
    }
  });

  const handlePblCitizenAuthSuccess = async (authData: PblAuthTokenResponse) => {
    await dispatch(
      signIn({
        isLogin: true,
        accessToken: authData.access_token,
        userData: authData.user,
        applications: authData.applications,
        registDone: true,
        refreshTokenTime: getAuthRefreshDeadline(authData.expires_in),
      })
    );

    navigate(resolvePblLoginRedirect(authData.user, authData.applications, searchParams.get('path')), {
      replace: true,
    });
  };

  const requestCitizenSmsOtp = async () => {
    const normalizedPhone = normalizeHongKongMobile(citizenPhone);
    const identityPartial = citizenIdentityPartial.trim();

    if (!normalizedPhone) {
      setCitizenPasswordlessStatus({
        tone: 'error',
        message: t('pages.auth.pblCitizenSmsLogin.phoneValidation'),
      });
      return;
    }

    if (!identityPartial) {
      setCitizenPasswordlessStatus({
        tone: 'error',
        message: t('pages.auth.pblCitizenSmsLogin.identityRequired'),
      });
      return;
    }

    setIsRequestingCitizenOtp(true);
    setCitizenPasswordlessStatus(null);

    try {
      const response = await issuePblCitizenSmsLoginOtp({
        ...normalizedPhone,
        identity_partial: identityPartial,
        ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
      });

      const bypassCode = response.data.data?.bypass_code;
      setCitizenPasswordlessStatus({
        tone: 'success',
        message: bypassCode
          ? t('pages.auth.pblCitizenSmsLogin.otpIssuedTestCode', { code: bypassCode })
          : t('pages.auth.pblCitizenSmsLogin.otpSent'),
      });
    } catch (error: any) {
      const message =
        error.response?.data?.message || getApiErrorMessage(error, t('pages.auth.pblCitizenSmsLogin.requestFailed'));
      setCitizenPasswordlessStatus({ tone: 'error', message });
      errorHandler(message);
    } finally {
      setIsRequestingCitizenOtp(false);
      if (turnstileSiteKey) resetTurnstile();
    }
  };

  const verifyCitizenSmsOtp = async () => {
    const normalizedPhone = normalizeHongKongMobile(citizenPhone);
    const identityPartial = citizenIdentityPartial.trim();
    const otp = citizenSmsOtp.trim();

    if (!normalizedPhone || !identityPartial || !/^\d{6}$/.test(otp)) {
      setCitizenPasswordlessStatus({
        tone: 'error',
        message: t('pages.auth.pblCitizenSmsLogin.otpValidation'),
      });
      return;
    }

    setIsVerifyingCitizenOtp(true);

    try {
      const response = await verifyPblCitizenSmsLoginOtp({
        ...normalizedPhone,
        identity_partial: identityPartial,
        otp,
      });

      await handlePblCitizenAuthSuccess(response.data.data);
    } catch (error: any) {
      const message =
        error.response?.data?.message || getApiErrorMessage(error, t('pages.auth.pblCitizenSmsLogin.requestFailed'));
      setCitizenPasswordlessStatus({ tone: 'error', message });
      errorHandler(message);
    } finally {
      setIsVerifyingCitizenOtp(false);
    }
  };

  const authMethodRadios = (
    <Box marginTop={15}>
      <Flex flexDirection={isMobile ? 'column' : 'row'}>
        <Flex flexDirection="column" gridGap="10px">
          <div className="radio">
            <FormControlLabel
              control={
                <Radio
                  color="success"
                  checked={loginMode === 'emailOtp'}
                  onChange={() => handleLoginModeChange('emailOtp')}
                  name="opt-buttons"
                />
              }
              label={t('pages.auth.otpMethods.email')}
              labelPlacement="end"
            />
          </div>
        </Flex>
        {showMemberSmsOtpLogin && (
          <Flex flexDirection="column" gridGap="10px">
            <div className="radio">
              <FormControlLabel
                control={
                  <Radio
                    color="success"
                    onChange={() => handleLoginModeChange('smsOtp')}
                    checked={loginMode === 'smsOtp'}
                    name="opt-buttons"
                  />
                }
                label={t('pages.auth.otpMethods.sms')}
                labelPlacement="end"
              />
            </div>
          </Flex>
        )}
        {showPblCitizenPasswordlessLogin && (
          <Flex flexDirection="column" gridGap="10px">
            <div className="radio">
              <FormControlLabel
                control={
                  <Radio
                    color="success"
                    onChange={() => handleLoginModeChange('pblCitizenSmsOtp')}
                    checked={loginMode === 'pblCitizenSmsOtp'}
                    name="opt-buttons"
                  />
                }
                label={t('pages.auth.pblCitizenSmsLogin.otpOptionLabel')}
                labelPlacement="end"
              />
            </div>
          </Flex>
        )}
      </Flex>
    </Box>
  );

  return (
    <>
      <LoadingPage isLoading={isLoading} />
      <Text marginTop={15}>{t('pages.auth.signIn.illustrate.step1')}</Text>
      {(searchParams.get('external_binding') === '1' || searchParams.get('wechat_binding') === '1') && (
        <StatusMessage tone="info">{t('pages.auth.external.bindingRequired')}</StatusMessage>
      )}
      {authMethodRadios}
      {!showCitizenSmsLoginForm ? (
        <form onSubmit={onSubmit}>
          <InputWrapper>
            <Controller
              control={control}
              defaultValue=""
              name={'email'}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                  label={t('form.email.label')}
                  placeholder={t('form.email.placeholder')}
                  variant="standard"
                  value={value}
                  sx={{ width: '100%', paddingBottom: '30px' }}
                  onChange={onChange}
                  error={!!error}
                  helperText={error ? error.message : null}
                  autoComplete="off"
                />
              )}
              rules={emailRegister}
            />
            <Controller
              control={control}
              defaultValue=""
              name={'password'}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Flex flexDirection={'column'} alignItems={'flex-end'}>
                  <TextField
                    label={t('form.password.label')}
                    placeholder={t('form.password.placeholder')}
                    variant="standard"
                    value={value}
                    sx={{ width: '100%', paddingBottom: '15px' }}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    type={'password'}
                    autoComplete="off"
                  />
                  <StyledLink to={PATH.auth.forgotPassword}>{t('forgotPassword')}</StyledLink>
                </Flex>
              )}
              rules={passwordRegister}
            />
            {loginMode === 'smsOtp' && !useOtpV2 && (
              <MultiInputWrapper marginBottom={0}>
                <Box style={{ width: isMobile ? '100%' : '170px' }}>
                  <FormSelect
                    control={control}
                    data={HONG_KONG_COUNTRY_CODE_OPTIONS}
                    inputLabel={t('form.countryCode.label')}
                    selectLabel={t('form.countryCode.label')}
                    name={'phone_country_code'}
                    defaultValue="+852"
                    disabled
                    register={{ required: t('required.countryCode') }}
                  />
                </Box>
                <Controller
                  control={control}
                  defaultValue=""
                  name={'phone_number'}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField
                      label={t('form.mobile.label')}
                      placeholder={t('form.mobile.placeholder')}
                      variant="standard"
                      value={value}
                      sx={{ width: '100%' }}
                      onChange={onChange}
                      error={!!error}
                      helperText={error ? error.message : null}
                      autoComplete="off"
                    />
                  )}
                  rules={{ required: t('required.mobile') }}
                />
              </MultiInputWrapper>
            )}
          </InputWrapper>

          {turnstileSiteKey ? (
            <TurnstileWidget
              key={`member-login-${turnstileResetKey}`}
              siteKey={turnstileSiteKey}
              action="member_login"
              onTokenChange={handleTurnstileTokenChange}
            />
          ) : null}

          <Box>
            <Flex gridGap={'10px'} marginBottom={15} flexDirection={isMobile ? 'column' : 'row'}>
              <Button
                size={'lg'}
                variant={'primary'}
                disabled={!isDirty || failedCounter > 5 || (Boolean(turnstileSiteKey) && !turnstileToken)}
              >
                {t('buttons.signIn')}
              </Button>
              <Button onClick={() => navigate('/')} type={'button'} size={'lg'} variant={'primary'}>
                {t('buttons.cancel')}
              </Button>
            </Flex>
          </Box>
        </form>
      ) : (
        <Box marginTop={15}>
          <InputWrapper>
            <TextField
              label={t('pages.auth.pblCitizenSmsLogin.mobileNumber')}
              value={citizenPhone}
              onChange={(event) => {
                setCitizenPhone(event.target.value);
                setCitizenPasswordlessStatus(null);
              }}
              variant="standard"
              fullWidth
              sx={{ paddingBottom: '30px' }}
              autoComplete="tel"
            />
            <TextField
              label={t('pages.auth.pblCitizenSmsLogin.identityLast4')}
              value={citizenIdentityPartial}
              onChange={(event) => {
                setCitizenIdentityPartial(event.target.value);
                setCitizenPasswordlessStatus(null);
              }}
              variant="standard"
              fullWidth
              sx={{ paddingBottom: '20px' }}
              autoComplete="off"
            />
          </InputWrapper>
          {citizenPasswordlessStatus && (
            <Text margin={'0 0 15px'} color={citizenPasswordlessStatus.tone === 'success' ? '#167A3A' : '#D32F2F'}>
              {citizenPasswordlessStatus.message}
            </Text>
          )}
          <TextField
            label={t('pages.auth.pblCitizenSmsLogin.otpLabel')}
            value={citizenSmsOtp}
            onChange={(event) => {
              setCitizenSmsOtp(event.target.value);
              setCitizenPasswordlessStatus(null);
            }}
            variant="standard"
            fullWidth
            sx={{ paddingBottom: '20px' }}
            autoComplete="one-time-code"
          />
          {turnstileSiteKey ? (
            <TurnstileWidget
              key={`pbl-citizen-login-${turnstileResetKey}`}
              siteKey={turnstileSiteKey}
              action="pbl_citizen_sms_login"
              onTokenChange={handleTurnstileTokenChange}
            />
          ) : null}
          <Flex gridGap={'10px'} flexDirection={isMobile ? 'column' : 'row'}>
            <Button
              type="button"
              size={'lg'}
              variant={'primary-invert'}
              rounded={false}
              disabled={
                isRequestingCitizenOtp || isVerifyingCitizenOtp || (Boolean(turnstileSiteKey) && !turnstileToken)
              }
              onClick={requestCitizenSmsOtp}
            >
              {isRequestingCitizenOtp
                ? t('pages.auth.pblCitizenSmsLogin.sending')
                : t('pages.auth.pblCitizenSmsLogin.sendSmsOtp')}
            </Button>
            <Button
              type="button"
              size={'lg'}
              variant={'primary'}
              rounded={false}
              disabled={isVerifyingCitizenOtp || isRequestingCitizenOtp}
              onClick={verifyCitizenSmsOtp}
            >
              {isVerifyingCitizenOtp
                ? t('pages.auth.pblCitizenSmsLogin.verifying')
                : t('pages.auth.pblCitizenSmsLogin.verifyAndSignIn')}
            </Button>
          </Flex>
        </Box>
      )}
      <ExternalProviderOptions
        mode="login"
        providers={externalProviders}
        disabled={Boolean(turnstileSiteKey) && !turnstileToken}
        onSelect={beginExternalLogin}
      />
      <Text>
        <StyledLink
          to={v2UiEnabled() ? withDmpPurchaseReturnPath(PATH.auth.signUp, searchParams.get('path')) : PATH.auth.signUp}
        >
          {t('registration')}
        </StyledLink>
      </Text>
    </>
  );
};

const Step2 = ({ loginData }: { loginData: LoginDataProps }) => {
  const navigate = useNavigate();
  const { isMobile } = useResize();
  const { termsDate, policyDate } = useGetPolicyTerms();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [attemptCounter, setAttemptCounter] = useState(0);
  const [currentToken, setCurrentToken] = useState(loginData.token);
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState('');
  const [availableAt, setAvailableAt] = useState(
    () => Date.parse(loginData.resendAvailableAt || '') || Date.now() + 60_000
  );
  const [now, setNow] = useState(Date.now());
  const resendPending = React.useRef(false);
  const verificationPending = React.useRef(false);
  const remaining = Math.max(0, Math.ceil((availableAt - now) / 1000));
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const dispatch = useDispatch();
  // @ts-ignore
  const { t, i18n } = useTranslation();

  const {
    handleSubmit,
    control,
    setError,
    setValue,
    clearErrors,
    formState: { isDirty },
  } = useForm<MemberSignInTokenFormFields>();

  const { tokenRegister } = useAuthValidation();

  const onSubmit = handleSubmit(async (data) => {
    if (verificationPending.current || resendPending.current) return;
    verificationPending.current = true;
    setIsLoading(true);
    const rawData = {
      ...data,
      one_time_token: currentToken,
      password: loginData.password,
      email: loginData.email,
    };

    try {
      const res = (await loginToken(rawData)).data;

      if (res.success) {
        const isPblLogin = isPblUser(res.data.user);

        if (isPblLogin) {
          await dispatch(
            signIn({
              isLogin: true,
              accessToken: res.data.access_token,
              userData: res.data.user,
              applications: res.data.applications,
              registDone: true,
              refreshTokenTime: getAuthRefreshDeadline(res.data.expires_in),
            })
          );

          navigate(resolvePblLoginRedirect(res.data.user, res.data.applications, searchParams.get('path')), {
            replace: true,
          });
          return;
        }

        if (res.data.user.stage < 4) {
          await dispatch(
            signIn({
              isLogin: true,
              accessToken: res.data.access_token,
              userData: res.data.user,
              applications: res.data.applications,
              registDone: false,
              refreshTokenTime: getAuthRefreshDeadline(res.data.expires_in),
            })
          );
          navigate(
            v2UiEnabled()
              ? withDmpPurchaseReturnPath(`/registration?step=${res.data.user.stage + 1}`, searchParams.get('path'))
              : `/registration?step=${res.data.user.stage + 1}`,
            { replace: true }
          );
          return;
        } else {
          if (res.data.user.lang) {
            i18n.changeLanguage(res.data.user.lang);
          } else {
            await switchLang({ lang: i18n.language });
          }
          await dispatch(
            signIn({
              isLogin: true,
              accessToken: res.data.access_token,
              userData: res.data.user,
              applications: res.data.applications,
              registDone: true,
              refreshTokenTime: getAuthRefreshDeadline(res.data.expires_in),
            })
          );
          const notificationRes = (await getUserNotifications()).data;
          dispatch(getNotification(notificationRes.data));
        }

        if (
          !res.data.user.tcAgreementAcceptedAt ||
          !res.data.user.PICsAgreementAcceptedAt ||
          new Date(res.data.user.tcAgreementAcceptedAt).getTime() < new Date(termsDate).getTime() ||
          new Date(res.data.user.PICsAgreementAcceptedAt).getTime() < new Date(policyDate).getTime()
        ) {
          navigate(
            v2UiEnabled()
              ? withDmpPurchaseReturnPath('/termsUpdate', res.data.return_path || searchParams.get('path'))
              : '/termsUpdate',
            { replace: true }
          );
          return;
        }

        const returnPath =
          safeInternalReturnPath(res.data.return_path || searchParams.get('path')) ||
          (v2UiEnabled() ? resolveDmpPurchaseReturnPath(null, String(res.data.user.id)) : null);
        if (returnPath) {
          navigate(returnPath, { replace: true });
        } else {
          navigate('/home', { replace: true });
        }
      }
    } catch (error: any) {
      let errorData = '';
      if (error.response?.status === 429) {
        errorData = error.response.data.message;
      } else {
        errorData = error.response?.data?.message || t('error.tryAgain');
      }
      setAttemptCounter(attemptCounter + 1);
      setError('code', { type: 'custom', message: errorData });
    } finally {
      verificationPending.current = false;
      setIsLoading(false);
    }
  });

  const resend = async () => {
    if (remaining > 0 || resendPending.current || verificationPending.current) return;
    resendPending.current = true;
    setResending(true);
    setResendError('');
    try {
      const response = await resendLoginCode(currentToken);
      if (!response.data.success) throw new Error(t('error.tryAgain'));
      setCurrentToken(response.data.data.one_time_token);
      setValue('code', '', { shouldDirty: false });
      clearErrors('code');
      setAvailableAt(Date.parse(response.data.data.resend_available_at || '') || Date.now() + 60_000);
      setNow(Date.now());
    } catch (error: any) {
      setResendError(error.response?.data?.message || error.message || t('error.tryAgain'));
      if (
        error.response?.status === 429 ||
        error.response?.data?.errors?.challenge?.includes('resend_cooldown_active')
      ) {
        const seconds = Number(
          error.response?.headers?.['retry-after'] || error.response?.data?.data?.retry_after || 60
        );
        setAvailableAt(Date.now() + (Number.isFinite(seconds) && seconds > 0 ? seconds : 60) * 1000);
        setNow(Date.now());
      }
    } finally {
      resendPending.current = false;
      setResending(false);
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <LoadingPage isLoading={isLoading} />
      <Text>{t('pages.auth.signIn.illustrate.step2')}</Text>
      <InputWrapper>
        <Controller
          control={control}
          defaultValue=""
          name={'code'}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              label={`${t('form.code.label')} ${attemptCounter > 0 ? `(${t('attempt')} ${attemptCounter})` : ''}`}
              variant="standard"
              value={value}
              sx={{ width: '100%', paddingBottom: '30px' }}
              onChange={onChange}
              error={!!error}
              helperText={error ? error.message : null}
              autoComplete="off"
            />
          )}
          rules={tokenRegister}
        />
      </InputWrapper>
      <Box>
        <Flex gridGap={'10px'} flexDirection={isMobile ? 'column' : 'row'}>
          <Button size={'lg'} variant={'primary'} disabled={!isDirty || attemptCounter > 4 || isLoading || resending}>
            {t('buttons.submit')}
          </Button>
          <Button
            type="button"
            size="lg"
            variant="primary-invert"
            disabled={remaining > 0 || resending || isLoading}
            onClick={() => void resend()}
          >
            {t('buttons.resendCode')}
          </Button>
          <Button onClick={() => navigate('/')} type={'button'} size={'lg'} variant={'primary'}>
            {t('buttons.cancel')}
          </Button>
        </Flex>
        {remaining > 0 && (
          <Text role="status">{t('pages.auth.forgotPassword.resendAvailableIn', { seconds: remaining })}</Text>
        )}
        {resendError && <Text role="alert">{resendError}</Text>}
      </Box>
    </form>
  );
};

export { Step1, Step2 };
